import { useId, useMemo, useState } from 'react';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Pagination,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ja, enUS } from 'date-fns/locale';
import {
  keisukeDiscussionService,
  type KeisukeDiscussionComment,
  type KeisukeDiscussionThreadDetail,
  type KeisukeDiscussionThreadListItem,
} from '../services/keisukeDiscussion.service';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import ConfirmDestructiveModal from '../components/common/ConfirmDestructiveModal';

function getDateFnsLocale(lang: string) {
  return lang === 'ja' ? ja : enUS;
}

function statusVariant(status: string): string {
  if (status === 'resolved') return 'success';
  if (status === 'locked') return 'secondary';
  return 'primary';
}

function discussionStatusLabel(status: string, t: (key: string) => string): string {
  switch (status) {
    case 'open':
      return t('keisuke.discussion.statusOpen');
    case 'resolved':
      return t('keisuke.discussion.statusResolved');
    case 'locked':
      return t('keisuke.discussion.statusLocked');
    default:
      return status;
  }
}

function extractApiError(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const res = (error as { response?: { data?: { error?: { message?: string } } } }).response;
    const msg = res?.data?.error?.message;
    if (msg) return msg;
  }
  if (error instanceof Error) return error.message;
  return '';
}

function DiscussionList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const dateLocale = getDateFnsLocale(i18n.language);
  const fieldId = useId();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<'recent' | 'popular'>('recent');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [analysisIdRaw, setAnalysisIdRaw] = useState('');
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const [formError, setFormError] = useState('');

  const listQuery = useQuery({
    queryKey: ['keisukeDiscussion', 'threads', { page, sort, status, q }],
    queryFn: () =>
      keisukeDiscussionService.list({
        page,
        limit: 15,
        sort,
        status: status || undefined,
        q: q.trim() || undefined,
      }),
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const tags = tagsRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      let analysisId: number | null | undefined;
      if (analysisIdRaw.trim()) {
        const n = parseInt(analysisIdRaw.trim(), 10);
        if (!Number.isFinite(n) || n < 1) {
          return Promise.reject(new Error('invalid analysis id'));
        }
        analysisId = n;
      }
      return keisukeDiscussionService.createThread({
        title: title.trim(),
        content: body.trim(),
        tags: tags.length ? tags : undefined,
        analysisId: analysisId ?? undefined,
        priority,
      });
    },
    onSuccess: (res) => {
      setFormError('');
      setTitle('');
      setBody('');
      setTagsRaw('');
      setAnalysisIdRaw('');
      void queryClient.invalidateQueries({ queryKey: ['keisukeDiscussion', 'threads'] });
      navigate(`/keisuke/discussion/${res.data.id}`);
    },
    onError: (error) => {
      const detail = extractApiError(error);
      setFormError(
        detail ||
          t('keisuke.discussion.createError')
      );
    },
  });

  const threads: KeisukeDiscussionThreadListItem[] = listQuery.data?.data ?? [];
  const pagination = listQuery.data?.pagination;

  const onSubmitNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    createMutation.mutate();
  };

  return (
    <Container fluid className="py-3" style={{ maxWidth: 960 }}>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <h1 className="h4 mb-0">{t('keisuke.discussion.title')}</h1>
        <Button as={Link as any} to="/keisuke" variant="outline-primary" size="sm">
          {t('keisuke.discussion.back')}
        </Button>
      </div>

      <Row className="g-3">
        <Col lg={7}>
          <h2 className="h6 text-muted border-bottom pb-2 mb-3">
            {t('keisuke.discussion.threadList')}
          </h2>
          {listQuery.isLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {listQuery.isError && (
            <Alert variant="danger">{t('keisuke.discussion.loadError')}</Alert>
          )}
          {!listQuery.isLoading && threads.length === 0 && (
            <p className="text-muted small mb-0">{t('keisuke.discussion.empty')}</p>
          )}
          {threads.map((th) => (
            <Card key={th.id} className="mb-2">
              <Card.Body className="py-2 px-3">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <Link to={`/keisuke/discussion/${th.id}`} className="fw-semibold text-decoration-none">
                      {th.title}
                    </Link>
                    <div className="small text-muted mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                      {th.excerpt}
                    </div>
                    <div className="small mt-2 d-flex flex-wrap gap-1 align-items-center">
                      <Badge bg={statusVariant(th.status)}>{discussionStatusLabel(th.status, t)}</Badge>
                      {th.tags.map((tg) => (
                        <Badge key={tg.id} bg="light" text="dark">
                          {tg.tag}
                        </Badge>
                      ))}
                      <span className="text-muted">
                        {t('keisuke.discussion.author')}: {th.user.username}
                      </span>
                      <span className="text-muted">
                        · {t('keisuke.discussion.updated')}:{' '}
                        {format(new Date(th.updatedAt), 'yyyy-MM-dd HH:mm', { locale: dateLocale })}
                      </span>
                      <span className="text-muted">
                        · {t('keisuke.discussion.views')} {th.viewCount}
                      </span>
                      <span className="text-muted">
                        · {t('keisuke.discussion.commentsCount')} {th.commentCount}
                      </span>
                    </div>
                  </div>
                  <Button as={Link as any} to={`/keisuke/discussion/${th.id}`} variant="outline-secondary" size="sm">
                    {t('keisuke.discussion.openThread')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
          {pagination && pagination.totalPages > 1 && (
            <Pagination className="mt-3 mb-0 flex-wrap">
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
                  {p}
                </Pagination.Item>
              ))}
            </Pagination>
          )}
        </Col>
        <Col lg={5}>
          <h2 className="h6 text-muted border-bottom pb-2 mb-3">
            {t('keisuke.discussion.newThread')}
          </h2>
          {!isAuthenticated && (
            <Alert variant="info" className="small">
              {t('keisuke.discussion.loginRequired')}
            </Alert>
          )}
          <Form onSubmit={onSubmitNew}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1" htmlFor={`${fieldId}-new-title`}>
                {t('keisuke.discussion.titleLabel')}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-new-title`}
                name="discussionNewTitle"
                size="sm"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                disabled={!isAuthenticated || createMutation.isPending}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1" htmlFor={`${fieldId}-new-body`}>
                {t('keisuke.discussion.bodyLabel')}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-new-body`}
                name="discussionNewBody"
                as="textarea"
                rows={5}
                size="sm"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={5000}
                disabled={!isAuthenticated || createMutation.isPending}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1" htmlFor={`${fieldId}-new-tags`}>
                {t('keisuke.discussion.tagsHint')}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-new-tags`}
                name="discussionNewTags"
                size="sm"
                value={tagsRaw}
                onChange={(e) => setTagsRaw(e.target.value)}
                placeholder={t('keisuke.discussion.tagsPlaceholder')}
                disabled={!isAuthenticated || createMutation.isPending}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1" htmlFor={`${fieldId}-new-analysis-id`}>
                {t('keisuke.discussion.analysisIdLabel')}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-new-analysis-id`}
                name="discussionNewAnalysisId"
                size="sm"
                inputMode="numeric"
                value={analysisIdRaw}
                onChange={(e) => setAnalysisIdRaw(e.target.value)}
                disabled={!isAuthenticated || createMutation.isPending}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small mb-1" htmlFor={`${fieldId}-new-priority`}>
                {t('keisuke.discussion.priorityLabel')}
              </Form.Label>
              <Form.Select
                id={`${fieldId}-new-priority`}
                name="discussionNewPriority"
                size="sm"
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'high' | 'medium' | 'low')}
                disabled={!isAuthenticated || createMutation.isPending}
              >
                <option value="high">{t('keisuke.discussion.priorityHigh')}</option>
                <option value="medium">{t('keisuke.discussion.priorityMedium')}</option>
                <option value="low">{t('keisuke.discussion.priorityLow')}</option>
              </Form.Select>
            </Form.Group>
            {formError && (
              <Alert variant="danger" className="py-2 small">
                {formError}
              </Alert>
            )}
            <Button type="submit" variant="primary" size="sm" disabled={!isAuthenticated || createMutation.isPending}>
              {createMutation.isPending ? <Spinner animation="border" size="sm" /> : t('keisuke.discussion.create')}
            </Button>
          </Form>

          <hr className="my-4" />
          <Form.Group className="mb-2">
            <Form.Label className="small" htmlFor={`${fieldId}-filter-status`}>
              {t('keisuke.discussion.filterStatus')}
            </Form.Label>
            <Form.Select
              id={`${fieldId}-filter-status`}
              name="discussionFilterStatus"
              size="sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('keisuke.discussion.all')}</option>
              <option value="open">{t('keisuke.discussion.statusOpen')}</option>
              <option value="resolved">{t('keisuke.discussion.statusResolved')}</option>
              <option value="locked">{t('keisuke.discussion.statusLocked')}</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small" htmlFor={`${fieldId}-list-sort`}>
              {t('keisuke.discussion.sortRecent')}
            </Form.Label>
            <Form.Select
              id={`${fieldId}-list-sort`}
              name="discussionListSort"
              size="sm"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as 'recent' | 'popular');
                setPage(1);
              }}
            >
              <option value="recent">{t('keisuke.discussion.sortRecent')}</option>
              <option value="popular">{t('keisuke.discussion.sortPopular')}</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small" htmlFor={`${fieldId}-list-search`}>
              {t('keisuke.discussion.search')}
            </Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                id={`${fieldId}-list-search`}
                name="discussionListSearch"
                size="sm"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setPage(1);
                }}
              />
              <Button size="sm" variant="outline-secondary" type="button" onClick={() => setPage(1)}>
                {t('common.ok')}
              </Button>
            </div>
          </Form.Group>
        </Col>
      </Row>
    </Container>
  );
}

interface CommentCardProps {
  comment: KeisukeDiscussionComment;
  threadId: number;
  currentUserId?: number;
  isAdmin: boolean;
  threadStatus: string;
  onDeleted: () => void;
}

function CommentCard({ comment, threadId, currentUserId, isAdmin, threadStatus, onDeleted }: CommentCardProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateFnsLocale(i18n.language);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [editError, setEditError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const canEdit = currentUserId === comment.userId || isAdmin;
  const canDelete = currentUserId === comment.userId || isAdmin;

  const editMutation = useMutation({
    mutationFn: () =>
      keisukeDiscussionService.editComment(threadId, comment.id, { content: editContent.trim() }),
    onSuccess: () => {
      setEditing(false);
      setEditError('');
      onDeleted();
    },
    onError: (error) => {
      const detail = extractApiError(error);
      setEditError(detail || t('keisuke.discussion.editError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => keisukeDiscussionService.deleteComment(threadId, comment.id),
    onSuccess: onDeleted,
  });

  return (
    <>
      <Card className="mb-2">
        <Card.Body className="py-2 px-3">
        <div className="small text-muted mb-1 d-flex justify-content-between align-items-center">
          <span>
            {comment.user.username} · {format(new Date(comment.createdAt), 'yyyy-MM-dd HH:mm', { locale: dateLocale })}
          </span>
          {(canEdit || canDelete) && threadStatus !== 'locked' && (
            <div className="d-flex gap-1">
              {canEdit && !editing && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-muted"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => { setEditContent(comment.content); setEditing(true); }}
                >
                  {t('keisuke.discussion.edit')}
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-danger"
                  style={{ fontSize: '0.75rem' }}
                  disabled={deleteMutation.isPending}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  {t('keisuke.discussion.delete')}
                </Button>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <div>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              maxLength={8000}
            />
            {editError && <div className="text-danger small mt-1">{editError}</div>}
            <div className="d-flex gap-2 mt-2">
              <Button size="sm" variant="primary" disabled={editMutation.isPending} onClick={() => editMutation.mutate()}>
                {editMutation.isPending ? <Spinner size="sm" animation="border" /> : t('keisuke.discussion.save')}
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => { setEditing(false); setEditError(''); }}>
                {t('keisuke.discussion.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
            {comment.content}
          </div>
        )}
      </Card.Body>
    </Card>
      <ConfirmDestructiveModal
        show={deleteConfirmOpen}
        onHide={() => setDeleteConfirmOpen(false)}
        message={t('keisuke.discussion.deleteConfirm')}
        isLoading={deleteMutation.isPending}
        onConfirm={async () => {
          await deleteMutation.mutateAsync();
        }}
      />
    </>
  );
}

function DiscussionThreadView() {
  const { threadId } = useParams<{ threadId: string }>();
  const id = useMemo(() => parseInt(String(threadId), 10), [threadId]);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const dateLocale = getDateFnsLocale(i18n.language);
  const commentFieldId = useId();
  const [comment, setComment] = useState('');
  const [commentErr, setCommentErr] = useState('');
  const isAdmin = isAdminUser(user);

  const threadQuery = useQuery({
    queryKey: ['keisukeDiscussion', 'thread', id],
    queryFn: () => keisukeDiscussionService.getById(id),
    enabled: Number.isFinite(id) && id > 0,
  });

  const thread = threadQuery.data?.data as KeisukeDiscussionThreadDetail | undefined;

  const invalidateThread = () => {
    void queryClient.invalidateQueries({ queryKey: ['keisukeDiscussion', 'thread', id] });
    void queryClient.invalidateQueries({ queryKey: ['keisukeDiscussion', 'threads'] });
  };

  const commentMutation = useMutation({
    mutationFn: () => keisukeDiscussionService.addComment(id, { content: comment.trim() }),
    onSuccess: () => {
      setComment('');
      setCommentErr('');
      invalidateThread();
    },
    onError: (error) => {
      const detail = extractApiError(error);
      setCommentErr(
        detail ||
          t('keisuke.discussion.commentError')
      );
    },
  });

  const resolveMutation = useMutation({
    mutationFn: () => keisukeDiscussionService.resolve(id),
    onSuccess: invalidateThread,
  });

  const reopenMutation = useMutation({
    mutationFn: () => keisukeDiscussionService.reopen(id),
    onSuccess: invalidateThread,
  });

  const lockMutation = useMutation({
    mutationFn: () => keisukeDiscussionService.lock(id),
    onSuccess: invalidateThread,
  });

  const unlockMutation = useMutation({
    mutationFn: () => keisukeDiscussionService.unlock(id),
    onSuccess: invalidateThread,
  });

  if (!Number.isFinite(id) || id < 1) {
    return (
      <Container className="py-4">
        <Alert variant="warning">{t('keisuke.discussion.invalidThreadId')}</Alert>
        <Button as={Link as any} to="/keisuke/discussion">
          {t('keisuke.discussion.back')}
        </Button>
      </Container>
    );
  }

  const canResolveOrReopen = thread && (isAdmin || user?.id === thread.userId);

  return (
    <Container fluid className="py-3" style={{ maxWidth: 880 }}>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/keisuke/discussion')}>
          ← {t('keisuke.discussion.threadList')}
        </Button>
        <Button as={Link as any} to="/keisuke" variant="outline-primary" size="sm">
          {t('keisuke.discussion.back')}
        </Button>
      </div>
      {threadQuery.isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}
      {threadQuery.isError && (
        <Alert variant="danger">{t('keisuke.discussion.loadError')}</Alert>
      )}
      {thread && (
        <>
          <h1 className="h4">{thread.title}</h1>
          <div className="small text-muted mb-2 d-flex flex-wrap gap-2 align-items-center">
            <Badge bg={statusVariant(thread.status)}>{discussionStatusLabel(thread.status, t)}</Badge>
            {thread.tags.map((tg) => (
              <Badge key={tg.id} bg="light" text="dark">
                {tg.tag}
              </Badge>
            ))}
            <span>
              {t('keisuke.discussion.author')}: {thread.user.username}
            </span>
            <span>
              {t('keisuke.discussion.created')}:{' '}
              {format(new Date(thread.createdAt), 'yyyy-MM-dd HH:mm', { locale: dateLocale })}
            </span>
            <span>
              {t('keisuke.discussion.views')}: {thread.viewCount}
            </span>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            {canResolveOrReopen && thread.status === 'open' && (
              <Button
                variant="outline-success"
                size="sm"
                disabled={resolveMutation.isPending}
                onClick={() => resolveMutation.mutate()}
              >
                {t('keisuke.discussion.resolve')}
              </Button>
            )}
            {canResolveOrReopen && thread.status === 'resolved' && (
              <Button
                variant="outline-warning"
                size="sm"
                disabled={reopenMutation.isPending}
                onClick={() => reopenMutation.mutate()}
              >
                {t('keisuke.discussion.reopen')}
              </Button>
            )}
            {isAdmin && thread.status !== 'locked' && (
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={lockMutation.isPending}
                onClick={() => lockMutation.mutate()}
              >
                {t('keisuke.discussion.lock')}
              </Button>
            )}
            {isAdmin && thread.status === 'locked' && (
              <Button
                variant="outline-primary"
                size="sm"
                disabled={unlockMutation.isPending}
                onClick={() => unlockMutation.mutate()}
              >
                {t('keisuke.discussion.unlock')}
              </Button>
            )}
          </div>

          <Card className="mb-4">
            <Card.Body>
              <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                {thread.content}
              </div>
            </Card.Body>
          </Card>
          <h2 className="h6 text-muted border-bottom pb-2 mb-3">
            {t('keisuke.discussion.comments')}
          </h2>
          {!isAuthenticated && (
            <Alert variant="info" className="small">
              {t('keisuke.discussion.loginRequired')}
            </Alert>
          )}
          {thread.comments.map((c) => (
            <CommentCard
              key={c.id}
              comment={c}
              threadId={id}
              currentUserId={user?.id}
              isAdmin={isAdmin}
              threadStatus={thread.status}
              onDeleted={invalidateThread}
            />
          ))}
          {isAuthenticated && (
            <Form
              className="mt-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!comment.trim()) return;
                commentMutation.mutate();
              }}
            >
              {commentErr && <Alert variant="danger" className="py-2 small">{commentErr}</Alert>}
              <Form.Group>
                <Form.Label className="small mb-1 visually-hidden" htmlFor={`${commentFieldId}-comment`}>
                  {t('keisuke.discussion.commentLabel')}
                </Form.Label>
                <Form.Control
                  id={`${commentFieldId}-comment`}
                  name="discussionComment"
                  as="textarea"
                  rows={3}
                  size="sm"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('keisuke.discussion.commentPlaceholder')}
                  maxLength={8000}
                  disabled={commentMutation.isPending || thread.status === 'locked'}
                />
              </Form.Group>
              <Button type="submit" variant="primary" size="sm" className="mt-2" disabled={commentMutation.isPending || thread.status === 'locked'}>
                {commentMutation.isPending ? <Spinner size="sm" animation="border" /> : t('keisuke.discussion.postComment')}
              </Button>
            </Form>
          )}
        </>
      )}
    </Container>
  );
}

export default function KeisukeDiscussionSection() {
  return (
    <Routes>
      <Route index element={<DiscussionList />} />
      <Route path=":threadId" element={<DiscussionThreadView />} />
    </Routes>
  );
}
